import {AfterViewInit, Component, Inject, Input, OnInit} from '@angular/core';
import CooperationModel from '../../../model/cooperation.model';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import CooperationService from '../../../api/cooperation/cooperation.service';
import Helper from '../../util/helper';
import {BehaviorSubject, Observable, of} from 'rxjs';
import CustomerModel from '../../../model/customer.model';
import Pagination from '../../../model/pagination';
import {debounceTime, finalize, switchMap, tap} from 'rxjs/operators';
import CustomerService from '../../../api/customer/customer.service';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import ArtistModel from '../../../model/artist.model';
import ArtistService from '../../../api/artist/artist.service';
import CooperationEventModel from '../../../model/cooperationEvent.model';
import EventService from '../../../api/event/event.service';
import {CustomerFormComponent, CustomerFormDialogModel} from '../../parts/customer/customer-form/customer-form.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {validateProperty} from '../../util/propertyRequired.validator';
import {markFormGroupTouched} from '../../util/touchForm';
import DoingService from '../../../api/doing/doing.service';
import DoingModel from '../../../model/doing.model';

@Component({
  selector: 'app-cooperation-dialog',
  templateUrl: './cooperation-dialog.component.html',
  styleUrls: ['./cooperation-dialog.component.scss']
})
export class CooperationDialogComponent implements OnInit, AfterViewInit {
  @Input() cooperation: CooperationModel;

  public customers$: Observable<Pagination<CustomerModel>>;
  public artists$: Observable<Pagination<ArtistModel>>;
  cooperationForm: FormGroup;
  public customersLoading: boolean;
  public artistsLoading: boolean;

  private eventsSubject = new BehaviorSubject<CooperationEventModel[]>([]);
  public events$ = this.eventsSubject.asObservable();

  private doingsSubject = new BehaviorSubject<DoingModel[]>([]);
  public doings$ = this.doingsSubject.asObservable();

  private savingSubject = new BehaviorSubject<boolean>(false);
  public saving$ = this.savingSubject.asObservable();

  constructor(private cooperationService: CooperationService,
              private customerService: CustomerService,
              private artistService: ArtistService,
              private eventService: EventService,
              private doingService: DoingService,
              private dialog: MatDialog,
              private snackBar: MatSnackBar,
              private fb: FormBuilder,
              public dialogRef: MatDialogRef<CooperationDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: CooperationDialogModel) {
    if (!this.data.cooperation) {
      this.cooperation = {
        insights: [],
        events: [],
        activities: [],
        notes: [],
        medias: [],
        tasks: [],
        platforms: [],
        state: 'open',
        artist: null,
        customer: null
      } as CooperationModel;
    } else {
      this.cooperation = this.data.cooperation;
    }
    this.cooperationForm = this.fb.group({
      id: this.cooperation.id,
      doing: [this.cooperation.doing],
      customer: [this.cooperation.customer, validateProperty('id', [Validators.required])],
      artist: [this.cooperation.artist, validateProperty('id', [Validators.required])],
      name: this.cooperation.name,
      state: this.cooperation.state,
      events: this.fb.array(this.buildArrayControl(this.cooperation.events))
    });
  }

  ngAfterViewInit() {
    this.cooperationForm.get('artist')
      .valueChanges
      .pipe(
        switchMap(content => {
          if(typeof content === 'object') {
            return of(content);
          } else {
            return of(null);
          }
        }),
      ).subscribe(artist => {
        if (artist == null) { return; }
        this.eventService.list(null, 1, 99, null, null, { artist: artist.id })
          .subscribe(events => {
            this.eventsSubject.next(events.results);
          });
    });

    if(this.cooperation.artist != null) {
      this.cooperationForm.get('artist').setValue(this.cooperation.artist);
    }
    this.customers$ = this.cooperationForm
      .get('customer')
      .valueChanges
      .pipe(
        debounceTime(300),
        tap(() => this.customersLoading = true),
        switchMap((value: string) => this.customerService.list(value, 1, 15, null, null, { state: 'customer' })),
        finalize(() => {
          this.customersLoading = false;
        })
      );

    this.artists$ = this.cooperationForm
      .get('artist')
      .valueChanges
      .pipe(
        debounceTime(300),
        tap(() => this.artistsLoading = true),
        switchMap((value: string) => this.artistService.list(value)),
        finalize(() => {
          this.artistsLoading = false;
        })
      );

    this.cooperationForm.valueChanges.subscribe(data => {
      if (data.artist != null && data.artist.id && data.customer != null && data.customer.id) {
        this.doingService.list(null, 1, 30, null, null, {
            artist: data.artist.id,
            customer: data.customer.id
        }).subscribe(result => {
          this.doingsSubject.next(result.results);
        });
      }
    });


  }

  save() {
    markFormGroupTouched(this.cooperationForm);
    if(!this.cooperationForm.valid) { return; }
    /* this prevents circular reference issue */
    this.cooperation.state = this.cooperationForm.get('state').value;
    this.cooperation.customer = this.cooperationForm.get('customer').value;
    this.cooperation.artist = this.cooperationForm.get('artist').value;
    this.cooperation.name = this.cooperationForm.get('name').value;
    this.cooperation.events = this.cooperationForm.get('events').value;

    // make currencies individual objects each since else it gets replaced by the circular reference helper
    for(let i = 0; i < this.cooperation.events.length; i++) {
      if(this.cooperation.events[i].currency) {
        this.cooperation.events[i].currency = JSON.parse(JSON.stringify(this.cooperation.events[i].currency));
      }
    }

    if(this.savingSubject.getValue()) { return; }

    let doing = this.cooperationForm.get('doing').value;

    const payload = JSON.parse(JSON.stringify(this.cooperation, Helper.circularReplacer()));

    delete payload.medias;
    delete payload.revenues;
    this.savingSubject.next(true);
    if (this.cooperation.id && typeof this.cooperation.id !== 'undefined') {
      this.cooperationService.update(payload).subscribe(result => {
        if (doing) {
          doing.cooperation = result;
          this.doingService.assignCooperation(doing, result).subscribe(d => {
            this.savingSubject.next(false);
            this.dialogRef.close(this.cooperation);
          });
        } else {
          this.savingSubject.next(false);
          this.dialogRef.close(this.cooperation);
        }
      }, error => {
        this.savingSubject.next(false);
      });
    } else {
      this.cooperationService.create(payload).subscribe(result => {
        if (result) {
          if (doing) {
            doing.cooperation = result;
            this.doingService.assignCooperation(doing, result).subscribe(d => {
              this.savingSubject.next(false);
              this.dialogRef.close(result);
            });
          } else {
            this.savingSubject.next(false);
            this.dialogRef.close(result);
          }
        }
      }, error => {
        this.snackBar.open('Fehler beim Speichern!');
        this.savingSubject.next(false);
      });
    }
  }

  ngOnInit(): void {
  }

  displayCustomerFn(customer: CustomerModel) {
    if (customer) {
      return customer.companyDisplayName + ' (' + customer.companyName + ')';
    }
  }

  displayDoingFn(doing: DoingModel) {
    if (doing) {
      return doing.text;
    }
  }

  displayArtistFn(artist: ArtistModel) {
    if (artist) {
      return artist.name;
    }
  }

  deleteEvent(index: number) {
    const eventsForm = this.cooperationForm.get('events') as FormArray;
    let event = eventsForm.at(index).value;
    if(event.id) {
      this.eventService.delete({ id: event.id } as any).subscribe(_ => {
        eventsForm.removeAt(index);
      }, error => {
        this.snackBar.open('Fehler beim Löschen!');
      });
    } else {
      eventsForm.removeAt(index);
    }

  }

  addEvent() {
    const eventsForm = this.cooperationForm.get('events') as FormArray;
    eventsForm.push(this.fb.group({
      briefing: '',
      coupon: '',
      fee: 0,
      title: ['', Validators.required],
      state: 'open',
      profile: [null],
      conditions: '',
      currency: [null],
      deadline: [null, Validators.required],
      fullday: true,
      accountingType: [null],
      socialContributionType: ['not_included', Validators.required],
      type: ['socialmedia', Validators.required]
    }));

  }

  buildArrayControl(data: CooperationEventModel[] | null): FormGroup[] {
    if (data) {
      return data.map(x => {
        console.log(x.changeable);
        return this.fb.group({
          id: [x.id],
          title: [x.title ? x.title : '', Validators.required],
          briefing: [x.briefing ? x.briefing : ''],
          deadline: [{value: x.deadline ? x.deadline : null, disabled: !x.changeable}, Validators.required],
          fullday: [{value: x.fullday ? x.fullday : true, disabled: !x.changeable}],
          fee: [{ value: x.fee ? x.fee : 0, disabled: !x.changeable }, Validators.required],
          coupon: [x.coupon ? x.coupon : ''],
          state: [x.state ? x.state : 'draft', Validators.required],
          currency: [{ value: x.currency, disabled: !x.changeable }],
          conditions: [x.conditions ? x.conditions : ''],
          accountingType: [{ value: x.accountingType || null, disabled: !x.changeable}],
          profile: [x.profile || null],
          socialContributionType: [{ value: x.socialContributionType, disabled: !x.changeable }, [Validators.required]],
          type: [x.type || 'socialmedia', [Validators.required]],
          changeable: [x.changeable, []]
        });
      });
    } else {
      return [];
    }
  }

  addCustomer() {
    const dialogData = new CustomerFormDialogModel({
      socialMedia: [],
    } as CustomerModel);

    const dialogRef = this.dialog.open(CustomerFormComponent, {
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.customerService.create(dialogResult).subscribe(result => {
          this.snackBar.open('Unternehmen wurde hinzugefügt');
          this.cooperationForm.get('customer').setValue(result);
        }, error => {
          this.snackBar.open('Fehler beim Hinzufügen des Unternehmens');
        });
      }
    });
  }
}

export interface CooperationDialogModel {
  cooperation?: CooperationModel;
}
